import React, { useEffect, useState } from "react";
import { Users, Receipt, AlertTriangle, Banknote, RefreshCw, Download, TrendingUp, Wallet, ChevronLeft, ChevronRight, Info } from "lucide-react";
import { api, formatGBPPrecise, formatNumber, formatDate, formatApiErrorDetail } from "../../lib/api";
import { KpiCard, Pill, PrimaryButton, GhostButton } from "../../components/shell";

const STATUS_TONE = (s) => ({
    "active": "active", "trialing": "active", "past_due": "pending",
    "canceled": "paused", "unpaid": "unpaid", "incomplete": "pending", "incomplete_expired": "cancelled",
}[s] || "neutral");

export default function FansTab({ tenantId, queryString }) {
    const [revenue, setRevenue] = useState(null);
    const [subs, setSubs] = useState({ data: [], has_more: false, cursor: null });
    const [failed, setFailed] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [balance, setBalance] = useState(null);
    const [tierSummary, setTierSummary] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const apiBase = process.env.REACT_APP_BACKEND_URL;

    const load = async (filter = statusFilter) => {
        setBusy(true);
        setErr("");
        try {
            const tenantQ = queryString || "";
            const sep = tenantQ ? "&" : "?";
            const [r, s, f, p, b, ts] = await Promise.all([
                api.get(`/stripe/revenue${tenantQ}`),
                api.get(`/stripe/subscribers${tenantQ}${sep}status=${filter}&limit=50`),
                api.get(`/stripe/failed-charges${tenantQ}`),
                api.get(`/stripe/payouts${tenantQ}${sep}limit=6`),
                api.get(`/stripe/balance${tenantQ}`),
                api.get(`/stripe/tier-summary${tenantQ}`).catch(() => ({ data: { tiers: [], unmatched_count: 0 } })),
            ]);
            setRevenue(r.data);
            setSubs({ data: s.data.data, has_more: s.data.has_more, cursor: s.data.data.length ? s.data.data[s.data.data.length - 1].id : null });
            setFailed(f.data.data);
            setPayouts(p.data.data);
            setBalance(b.data);
            setTierSummary(ts.data);
        } catch (e) {
            const code = e.response?.status;
            const detail = formatApiErrorDetail(e.response?.data?.detail);
            if (code === 409) setErr(detail || "This club isn't fully linked to Stripe yet — add the Connect account ID and metadata key/value in Settings.");
            else if (code === 424) setErr(detail || "Stripe rejected the request.");
            else setErr(detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, statusFilter]);

    const refresh = async () => {
        await api.post(`/stripe/refresh${queryString || ""}`);
        await load();
    };

    const download = async () => {
        const tenantQ = queryString || "";
        const r = await api.get(`/stripe/subscribers/export${tenantQ}`, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url; a.download = `subscribers-${tenantId || "export"}.csv`; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
    };

    return (
        <>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3">Fan revenue · live from Stripe Connect</div>
                    <h1 className="font-display font-black text-4xl md:text-5xl tracking-tightest leading-none">Subscribers &amp; payouts</h1>
                    <p className="font-editorial italic text-zinc-400 mt-3 text-lg">
                        Everything your fan app earns — subscriptions, charges, payouts, balance — straight from your Stripe account.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <GhostButton onClick={refresh} disabled={busy} data-testid="fans-refresh">
                        <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
                    </GhostButton>
                    <PrimaryButton tenantTheme onClick={download} data-testid="fans-export">
                        <Download size={14} strokeWidth={2.4} /> Export CSV
                    </PrimaryButton>
                </div>
            </div>

            {err && <div className="border border-rose-500/40 text-rose-300 p-6 text-sm" data-testid="fans-error">{err}</div>}

            {/* KPI strip — 5 cards: Active subs · MRR (with fee math) · Available · Pending · Month gross */}
            {revenue && (
                <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="fan-kpis">
                    <KpiCard label="Active subscribers" value={formatNumber(revenue.active_subscribers)} sub={revenue.past_due_subscribers ? `${revenue.past_due_subscribers} past due` : "all paid up"} testId="fan-kpi-active" accent />
                    <MrrKpiCard revenue={revenue} />
                    <BalanceKpiCard label="Available" tone="primary" balanceArr={balance?.available || []} hint="Funds ready to pay out to your bank." testId="fan-kpi-available" />
                    <BalanceKpiCard label="Pending" tone="muted" balanceArr={balance?.pending || []} hint={revenue?.payout_schedule ? `Payouts: ${revenue.payout_schedule}` : "Charges still inside Stripe's 7-day clearing window."} testId="fan-kpi-pending" />
                    <MtdGrossKpiCard revenue={revenue} />
                </section>
            )}

            {revenue?.payout_schedule && (
                <div className="text-[11px] text-zinc-500 -mt-1" data-testid="payout-schedule-note">
                    <span className="inline-flex items-center gap-1.5"><Wallet size={11} /> Payouts: <span className="text-zinc-300">{revenue.payout_schedule}</span></span>
                </div>
            )}

            {/* Tier strip — ALWAYS visible above the subscribers table. Empty state nudges configuration. */}
            <section className="border hairline p-5" data-testid="tier-summary">
                <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-4">Subscriber tiers</div>
                {tierSummary && tierSummary.tiers && tierSummary.tiers.length > 0 ? (
                    <div className="flex flex-wrap gap-3">
                        {tierSummary.tiers.map((t) => (
                            <div key={t.id} className="flex items-center gap-3 border hairline px-4 py-3 min-w-[180px]" data-testid={`tier-card-${t.id}`}>
                                {t.icon_path ? (
                                    <img src={`${apiBase}/api/files/${t.icon_path}`} alt={t.name} className="w-9 h-9 object-contain" />
                                ) : (
                                    <div className="w-9 h-9 flex items-center justify-center font-display font-extrabold text-sm" style={{ background: "var(--tenant-primary)", color: "var(--tenant-primary-foreground, #000)" }}>
                                        {(t.name || "?").slice(0, 2).toUpperCase()}
                                    </div>
                                )}
                                <div className="min-w-0">
                                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">{t.metadata_value || (t.monthly_amount_gbp ? `£${Number(t.monthly_amount_gbp).toFixed(2)}` : "—")}</div>
                                    <div className="font-display font-bold text-sm tracking-tight truncate">{t.name}</div>
                                </div>
                                <div className="ml-auto font-display font-extrabold text-2xl tracking-tightest" style={{ color: "var(--tenant-primary)" }}>{t.count}</div>
                            </div>
                        ))}
                        {tierSummary.unmatched_count > 0 && (
                            <div className="flex items-center gap-3 border hairline px-4 py-3 border-dashed" data-testid="tier-card-unmatched">
                                <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Unmatched</div>
                                <div className="font-display font-extrabold text-2xl tracking-tightest text-zinc-400">{tierSummary.unmatched_count}</div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-xs text-zinc-500 italic" data-testid="tier-summary-empty">
                        No tiers configured yet — once your subscription products are set up in Floodlight, your subscribers will be grouped here with icons and counts.
                    </div>
                )}
            </section>

            {/* Diagnostic panel — REMOVED from club UX; moved to /platform per-club card. */}

            {/* Subscribers table */}
            <section data-testid="subs-section">
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Subscribers</div>
                        <h2 className="font-display font-extrabold text-2xl tracking-tight">Every fan who pays</h2>
                    </div>
                    <div className="flex items-center gap-1 border hairline">
                        {["all", "active", "past_due", "canceled"].map((s) => (
                            <button key={s} onClick={() => setStatusFilter(s)}
                                className={`px-3 py-2 text-[11px] uppercase tracking-ultra-wide ${statusFilter === s ? "bg-white/[0.06] text-white" : "text-zinc-400 hover:text-white"}`}
                                data-testid={`subs-filter-${s}`}>
                                {s.replace("_", " ")}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="border hairline overflow-hidden">
                    <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b hairline text-[10px] uppercase tracking-ultra-wide text-zinc-500">
                        <div className="col-span-3">Subscriber</div>
                        <div className="col-span-2">Tier</div>
                        <div className="col-span-2">Plan</div>
                        <div className="col-span-2">Status</div>
                        <div className="col-span-2">Monthly</div>
                        <div className="col-span-1 text-right">Renews</div>
                    </div>
                    {subs.data.length === 0 ? (
                        <div className="px-6 py-12 text-center text-sm text-zinc-500" data-testid="subs-empty">No subscribers in this state yet.</div>
                    ) : subs.data.map((s, i) => (
                        <div key={s.id} className={`grid grid-cols-12 items-center px-6 py-4 text-sm hover:bg-white/[0.025] transition-colors ${i !== 0 ? "border-t hairline" : ""}`} data-testid={`sub-row-${s.id}`}>
                            <div className="col-span-12 md:col-span-3">
                                <div className="text-white truncate">{s.customer_name || s.customer_email || s.metadata?.user_id || "—"}</div>
                                <div className="text-[11px] text-zinc-500 truncate font-mono">
                                    {s.customer_email || (s.metadata?.user_id ? `uid · ${s.metadata.user_id}` : "")}
                                </div>
                            </div>
                            <div className="col-span-6 md:col-span-2">
                                {s.tier ? (
                                    <div className="inline-flex items-center gap-2 border hairline px-2 py-1" data-testid={`sub-tier-${s.id}`}>
                                        {s.tier.icon_path ? (
                                            <img src={`${apiBase}/api/files/${s.tier.icon_path}`} alt={s.tier.name} className="w-5 h-5 object-contain" />
                                        ) : (
                                            <span className="w-5 h-5 flex items-center justify-center text-[9px] font-bold" style={{ background: "var(--tenant-primary)", color: "#000" }}>
                                                {(s.tier.name || "?").slice(0, 1).toUpperCase()}
                                            </span>
                                        )}
                                        <span className="text-xs text-white truncate">{s.tier.name}</span>
                                    </div>
                                ) : (
                                    <span className="text-zinc-600 text-xs">—</span>
                                )}
                            </div>
                            <div className="col-span-6 md:col-span-2 text-zinc-300 truncate">{s.plan_nickname}</div>
                            <div className="col-span-6 md:col-span-2">
                                <Pill tone={STATUS_TONE(s.status)}>{s.status}{s.cancel_at_period_end ? " · ending" : ""}</Pill>
                            </div>
                            <div className="col-span-6 md:col-span-2 font-display font-bold tracking-tight">{formatGBPPrecise(s.monthly_gbp)}<span className="text-zinc-500 font-normal text-xs"> /mo</span></div>
                            <div className="col-span-6 md:col-span-1 md:text-right text-zinc-300 text-xs">{s.current_period_end ? formatDate(s.current_period_end) : "—"}</div>
                        </div>
                    ))}
                </div>
                <div className="text-[10px] text-zinc-600 mt-3">{subs.has_more ? "More available — use Export CSV for the full list." : `Showing ${subs.data.length} subscriber${subs.data.length === 1 ? "" : "s"}.`}</div>
            </section>

            {/* Payouts to bank — placed AFTER subscribers per the redesign */}
            <section className="border hairline p-7" data-testid="payouts-card">
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Payouts</div>
                        <h3 className="font-display font-extrabold text-xl tracking-tight">Recent transfers to your bank</h3>
                    </div>
                    <Banknote size={16} className="text-zinc-500" />
                </div>
                {payouts.length === 0 ? (
                    <div className="text-xs text-zinc-500 italic">No payouts yet. They land here when Stripe pays out to your linked bank.</div>
                ) : (
                    <div className="space-y-2.5">
                        {payouts.map((p) => (
                            <div key={p.id} className="flex items-center justify-between border-t hairline pt-3" data-testid={`payout-${p.id}`}>
                                <div className="min-w-0">
                                    <div className="text-sm text-white">{formatDate(p.arrival_date)}</div>
                                    <div className="text-[11px] text-zinc-500 font-mono truncate">{p.id}</div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Pill tone={p.status === "paid" ? "paid" : p.status === "in_transit" ? "pending" : "neutral"}>{p.status}</Pill>
                                    <div className="font-display font-bold tracking-tight w-20 text-right">{formatGBPPrecise(p.amount_gbp)}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Failed renewals — bottom of the page */}
            <section className="border hairline p-7" data-testid="failed-card">
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Failed payments</div>
                        <h3 className="font-display font-extrabold text-xl tracking-tight">Renewals to keep an eye on</h3>
                    </div>
                    <AlertTriangle size={16} className={failed.length ? "text-amber-400" : "text-zinc-500"} />
                </div>
                {failed.length === 0 ? (
                    <div className="text-xs text-zinc-500 italic">No failed renewals right now. Stripe will retry up to 4 times automatically before any subscription drops to past_due.</div>
                ) : (
                    <div className="space-y-2">
                        {failed.map((f) => (
                            <div key={f.id} className="grid grid-cols-12 items-center gap-3 border-t hairline pt-3 text-sm" data-testid={`failed-${f.id}`}>
                                <div className="col-span-12 md:col-span-4">
                                    <div className="text-white truncate">{f.customer_name || f.customer_email || "Customer"}</div>
                                    <div className="text-[11px] text-zinc-500 truncate">{f.customer_email}</div>
                                </div>
                                <div className="col-span-6 md:col-span-3 text-zinc-400 text-xs">
                                    {f.failure_message || "Awaiting retry"}
                                </div>
                                <div className="col-span-6 md:col-span-2 text-xs">
                                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Next retry</div>
                                    <div className="text-zinc-300">{f.next_payment_attempt ? formatDate(f.next_payment_attempt) : "—"}</div>
                                </div>
                                <div className="col-span-6 md:col-span-2">
                                    <Pill tone="unpaid">attempt {f.attempt_count}</Pill>
                                </div>
                                <div className="col-span-6 md:col-span-1 md:text-right font-display font-bold tracking-tight">{formatGBPPrecise(f.amount_due_gbp)}</div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </>
    );
}



/* ------------ Custom KPI cards ------------ */

// MRR card: NET is the headline number (what actually lands with the club after fee).
// Math line beneath shows the gross + fee deduction so it's transparent.
const MrrKpiCard = ({ revenue }) => {
    const gross = Number(revenue?.subscription_mrr_gbp || 0);
    const pct = Number(revenue?.commission_pct || 0);
    const fee = +(gross * pct / 100).toFixed(2);
    const net = +(gross - fee).toFixed(2);
    return (
        <div className="border hairline p-5" data-testid="fan-kpi-mrr">
            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Subscription MRR <span className="text-zinc-600">· net</span></div>
            <div className="font-display font-extrabold text-3xl md:text-4xl tracking-tightest mt-2">{formatGBPPrecise(net)}</div>
            {pct > 0 ? (
                <div className="text-[11px] mt-2 leading-relaxed" data-testid="fan-mrr-math">
                    <span className="text-zinc-500">{formatGBPPrecise(gross)} gross − {pct}% platform fee </span>
                    <span style={{ color: "var(--tenant-primary)" }}>{formatGBPPrecise(fee)}</span>
                </div>
            ) : (
                <div className="text-[11px] mt-2 text-zinc-500">normalised to monthly</div>
            )}
        </div>
    );
};

// Available / Pending compact KPI. Sums GBP rows if multiple.
const BalanceKpiCard = ({ label, tone, balanceArr, hint, testId }) => {
    const total = (balanceArr || []).reduce((acc, b) => acc + (b.amount_gbp || 0), 0);
    const color = tone === "primary" ? "var(--tenant-primary)" : "#a1a1aa";
    return (
        <div className="border hairline p-5" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">{label}</div>
            <div className="font-display font-extrabold text-3xl md:text-4xl tracking-tightest mt-2" style={{ color }}>{formatGBPPrecise(total)}</div>
            <div className="text-[11px] mt-2 text-zinc-500 leading-relaxed">{hint}</div>
        </div>
    );
};

// "This month gross" — label literally says gross so big number stays gross.
// A small line below shows the platform-fee deduction for transparency.
const MtdGrossKpiCard = ({ revenue }) => {
    const gross = Number(revenue?.month_to_date_gross_gbp || 0);
    const pct = Number(revenue?.commission_pct || 0);
    const fee = +(gross * pct / 100).toFixed(2);
    const net = +(gross - fee).toFixed(2);
    return (
        <div className="border hairline p-5" data-testid="fan-kpi-mtd">
            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">This month gross</div>
            <div className="font-display font-extrabold text-3xl md:text-4xl tracking-tightest mt-2">{formatGBPPrecise(gross)}</div>
            <div className="text-[11px] mt-2 leading-relaxed text-zinc-500">
                {`${revenue?.month_to_date_charge_count || 0} charges so far`}
                {pct > 0 && (
                    <>
                        <span className="block">
                            − {pct}% fee <span style={{ color: "var(--tenant-primary)" }}>{formatGBPPrecise(fee)}</span>
                            <span className="text-zinc-600"> · net </span>
                            <span className="text-white">{formatGBPPrecise(net)}</span>
                        </span>
                    </>
                )}
            </div>
        </div>
    );
};
